import React, { useState } from "react";
import { complement, isNil } from "ramda";
import {
  faAngleUp,
  faFax,
  faMapMarkerAlt,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";

import Canvas from "../View/DrawCanvas/DrawCanvas";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tags from "../Tags/Tags";
import TextWithTags from "../View/TextWithTags/TextWithTags";
import { createUseStyles } from "react-jss";
import { detect } from "detect-browser";
import { eventTypes } from "../../../lib/analytics";
import imageStylesObject from "../View/DrawCanvas/ImageStyles";
import mixins from "../../../styles/mixins";
import { openLinksByLocation } from "../../../lib/utils";

const useImageStyles = createUseStyles(imageStylesObject);

const useStyles = createUseStyles((theme) => ({
  resultsViewWrapper: {
    backgroundColor: "white",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "flex-start",
    borderRadius: "2px",
    margin: "2px 0 0 0",
    transition: "all 0.1s ease-in-out",
    "&:hover": {
      cursor: "pointer",
      boxShadow: "0 0 3px 1px rgba(0, 0, 0, 0.3)",
    },
    "&:active": { boxShadow: "0 0 0 0 rgba(0, 0, 0, 0.3)" },
  },
  upperResultsView: {
    display: "flex",
    position: "relative",
    justifyContent: "flex-start",
    alignItems: "stretch",
  },
  iconPath: { fill: theme.secondaryColor },
  imageWrapper: {
    maxWidth: "100px",
    flex: "1",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  contentWrapper: {
    flex: "5",
    display: "flex",
    position: "relative",
    flexDirection: "column",
    justifyContent: "flex-start",
    textAlign: "left",
    padding: "5px 0 10px 0",
  },
  resultTitle: {
    fontWeight: "500",
    margin: "5px 0 5px 0",
    fontSize: "1em",
    "&.clickable": { cursor: "pointer" },
  },
  subTitle: { fontSize: "0.9em", marginBottom: "10px" },
  mainPhone: {
    fontSize: "0.8em",
    marginTop: "5px",
    "&:hover": {
      cursor: "pointer",
      "svg.phoneIcon path": { fill: theme.secondaryColor },
      p: {
        color: theme.secondaryColor,
        textDecoration: "underline",
      },
    },
    "&:active": { opacity: "0.8" },
    "svg.phoneIcon": {
      width: "10px",
      // height: "auto",
      marginRight: "5px",
      path: { fill: theme.primaryColor },
    },
    p: { display: "inline-block", margin: "0" },
  },
  mainAddress: {
    fontSize: "0.8em",
    marginBottom: "5px",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    "&:hover": {
      cursor: "pointer",
      "& p": {
        color: theme.secondaryColor,
        textDecoration: "underline",
      },
    },
    "&:hover $markerIcon": {
      "& path": { fill: theme.secondaryColor },
    },
    "&:active": { opacity: "0.8" },

    "& p": { display: "inline-block", margin: "0" },
  },
  markerIcon: {
    width: "10px !important",
    // height: "auto",
    marginRight: "5px",
    "& path": { fill: theme.primaryColor },
  },
  moreButton: {
    position: "absolute",
    bottom: "5px",
    right: "5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "25px",
    height: "25px",
    borderRadius: "20px",
    backgroundColor: theme.primaryColor,
    zIndex: "2",

    "&:hover": {
      cursor: "pointer",
      backgroundColor: theme.secondaryColor,
    },
    "&:active": { backgroundColor: theme.primaryColor },
  },
  caretIcon: {
    position: "relative",
    top: "1px",
    width: "10px !important",
    // height: "auto",
    transform: "rotate(-180deg)",
    transition: "transform 300ms ease-in-out",
    "& path": { fill: theme.internalWhite },
  },
  caretIconOpen: {
    transform: "rotate(0deg)",
  },
  moreWithTags: { bottom: "25px" },
  bottomResultsView: {
    overflow: "hidden",
    "-ms-overflow-style": "none",
    maxHeight: "0",
    transition: "max-height 300ms ease-in-out",
  },
  bottomResultsViewOpen: { maxHeight: "1000px" },
  foldingInfo: {
    margin: "5px 20px 20px 20px",
    borderTop: `1px solid ${theme.primaryColor}`,

    "& span": {
      display: "flex",
      justifyContent: "flex-start",
      alignItems: "center",
    },
    "& svg": {
      marginRight: "5px",
      "& path": { fill: theme.secondaryColor },
    },
  },
  bottomAddressLink: {
    composes: "$addressLink",
    marginBottom: "5px",
    fontSize: "0.8em",
  },
  foldingAddressTitle: { margin: "10px 0 10px 0", fontSize: "1em" },
  phoneButton: {
    composes: "$contactBtn",
    margin: "5px 0 5px 0",
    fontSize: "0.8em",
  },
  faxButton: {
    composes: "$contactBtn",
    margin: "5px 0 5px 0",
    fontSize: "0.8em",
  },
  tagsContainer: { backgroundColor: "transparent", width: "50%" },
  tag: {
    height: "20px",
    lineHeight: "18px",
    padding: "0 12px",
    border: "0",
    borderRadius: "6px",
    backgroundColor: theme.primaryColor,
    display: "inline-block",
    color: "rgb(255, 255, 255)",
    margin: "0 3px 0 0",
    fontSize: "0",
    whiteSpace: "nowrap",
    width: "fit-content",
  },
  tagText: {
    fontSize: "13px",
    fontWeight: "600",
    verticalAlign: "middle",
    display: "inline-block",
  },
  ...mixins(theme),
}));

const Image = ({ avatarUrl, url, imageUrl, handleOnClick }) => {
  const styles = useImageStyles();
  return avatarUrl ? (
    <Canvas
      style={url ? { cursor: "pointer" } : {}}
      imageUrl={avatarUrl}
      onClick={handleOnClick}
    />
  ) : imageUrl ? (
    <div className={styles.imageContainer}>
      {imageUrl.startsWith("http") && (
        // TODO(nur): Temporary hack until cards are generalized.
        <img
          className={styles.resultImage}
          style={url ? { cursor: "pointer" } : {}}
          src={imageUrl}
          onClick={handleOnClick}
          alt={"product"}
        />
      )}
    </div>
  ) : null;
};

const Title = ({ clickable, text, handleOnClick, styles }) => (
  <h5
    className={styles.resultTitle + (clickable ? " " + styles.clickable : "")}
    style={text ? null : { margin: "0" }}
    onClick={handleOnClick}
  >
    {text}
  </h5>
);

const Subtitle = ({
  text,
  tagText,
  sendEvent,
  title,
  shouldTruncate,
  styles,
}) => (
  <div className={styles.subTitle} style={title ? null : { margin: "0" }}>
    {tagText ? (
      <TextWithTags
        text={text}
        sendEvent={sendEvent}
        shouldTruncate={shouldTruncate}
      />
    ) : (
      text
    )}
  </div>
);

const ShortAddress = ({ address, onAddressClick, styles }) => (
  <div className={styles.mainAddress} onClick={onAddressClick}>
    <FontAwesomeIcon
      className={styles.markerIcon}
      aria-hidden="true"
      icon={faMapMarkerAlt}
    />
    <p>{address}</p>
  </div>
);

const Phone = ({ phone, onPhoneClick, styles }) => (
  <div className={styles.mainPhone} onClick={onPhoneClick(phone)}>
    <FontAwesomeIcon
      className={styles.phoneIcon}
      aria-hidden="true"
      flip="horizontal"
      icon={faPhone}
    />
    <p>{phone}</p>
  </div>
);

const MoreButton = ({ tags, isDetailsOpen, setIsDetailsOpen, styles }) => (
  <div
    className={styles.moreButton}
    onClick={() => setIsDetailsOpen(!isDetailsOpen)}
  >
    <FontAwesomeIcon
      className={[
        styles.caretIcon,
        tags.length ? styles.moreWithTags : "",
        isDetailsOpen ? styles.caretIconOpen : "",
      ].join(" ")}
      aria-hidden="true"
      icon={faAngleUp}
    />
  </div>
);

const Detail = ({ data, onAddressClick, onPhoneClick, styles }) => {
  const { suggestions, title, address } = data;
  const phone = suggestions
    .map((suggestion) => suggestion.phone)
    .find((x) => x);
  const fax = suggestions.map((suggestion) => suggestion.fax).find((x) => x);

  return (
    <div className={styles.foldingInfo}>
      <div className={styles.foldingAddressTitle}>{title}</div>
      <span>
        <FontAwesomeIcon
          className={styles.markerIcon}
          aria-hidden="true"
          icon={faMapMarkerAlt}
        />
        <span
          className={styles.bottomAddressLink}
          onClick={() => onAddressClick(data)}
        >
          {address}
        </span>
      </span>
      {phone ? (
        <button className={styles.phoneButton} onClick={onPhoneClick(phone)}>
          <FontAwesomeIcon
            className={styles.phoneFaxIcon}
            icon={faPhone}
            flip="horizontal"
            aria-hidden={"true"}
          />
          <div>{phone}</div>
        </button>
      ) : null}
      {fax ? (
        <button className={styles.faxButton}>
          <FontAwesomeIcon
            className={styles.phoneFaxIcon}
            icon={faFax}
            aria-hidden={"true"}
          />
          <div>{fax}</div>
        </button>
      ) : null}
    </div>
  );
};

export default ({
  result,
  shouldTruncate,
  tagText,
  sendEvent,
  onAddressClick,
}) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState();
  const styles = useStyles();
  const mainAddress =
    result.address || result.edges.map((e) => e.address).find((x) => x);
  const mainPhone =
    result.phone || result.edges.map((e) => e.phone).find((x) => x);

  const handleOnClick = (event) => {
    event.stopPropagation();
    sendEvent(eventTypes.RESULT_CLICK, result);
    openLinksByLocation(result.url);
  };

  const onPhoneClick = (phone) => (event) => {
    event.stopPropagation();
    // Constructing an object similar to a regular suggestion.
    sendEvent(eventTypes.SUGGESTION_CLICK, { phone });
    window.location.href = "tel:" + phone;
  };

  let tags = result.price ? [result.price] : [];
  tags = tags.concat(result.tags);

  return (
    <div className={styles.resultsViewWrapper}>
      <div className={styles.upperResultsView}>
        <div className={styles.imageWrapper}>
          <Image
            avatarUrl={result.avatarUrl}
            handleOnClick={handleOnClick}
            imageUrl={result.imageUrl}
            url={result.url}
          />
        </div>
        <div
          className={styles.contentWrapper}
          style={detect().name === "ie" ? { flex: 3 } : {}}
        >
          <Title
            handleOnClick={handleOnClick}
            text={result.title}
            clickable={complement(isNil)(result.url)}
            styles={styles}
          />
          <Subtitle
            sendEvent={sendEvent}
            shouldTruncate={shouldTruncate}
            tagText={tagText}
            text={result.text}
            title={result.title}
            styles={styles}
          />
          {mainAddress && (
            <ShortAddress
              address={mainAddress}
              onAddressClick={() => onAddressClick(result)}
              styles={styles}
            />
          )}
          {mainPhone && (
            <Phone
              phone={mainPhone}
              onPhoneClick={onPhoneClick}
              styles={styles}
            />
          )}
          {tags.length ? <Tags tags={tags} useStyles={styles} /> : null}
        </div>
        {result.edges.length >= 2 && (
          <MoreButton
            isDetailsOpen={isDetailsOpen}
            setIsDetailsOpen={setIsDetailsOpen}
            tags={result.tags}
            styles={styles}
          />
        )}
      </div>
      <div
        className={[
          styles.bottomResultsView,
          isDetailsOpen ? styles.bottomResultsViewOpen : "",
        ].join(" ")}
      >
        {result.edges
          .filter((edge) => edge.address || edge.phone)
          .map((edge, i) => (
            <Detail
              key={i}
              data={edge}
              onPhoneClick={onPhoneClick}
              onAddressClick={onAddressClick}
              styles={styles}
            />
          ))}
      </div>
    </div>
  );
};
